import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { getNodesAvailable } from 'lib/api/decisionEngine';
import CustomButton from 'lib/designSystem/Button';
import {
  NEW_NODE,
  Node,
  RevisionObjective,
} from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import RangesAssign from './RangesAssignation';

type Props = {
  newNode: Node;
  setNewNode: (node: Node) => void;
  openAddNode: boolean;
  setOpenAddNode: (open: boolean) => void;
  decisionNodesAssignation: Node[];
  setDecisionNodesAssignation: (nodes: Node[]) => void;
  revisionObjective: RevisionObjective;
};

const NewMatrixAssignation: React.FC<Props> = ({
  newNode,
  setNewNode,
  openAddNode,
  setOpenAddNode,
  decisionNodesAssignation,
  setDecisionNodesAssignation,
  revisionObjective,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data } = getNodesAvailable(dispatch, revisionObjective);
  const decisionNodes = data?.decisionNodes || [];
  const categorizedNodes = data?.categorizedNodes || {};
  const [operation, setOperation] = useState('gt');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [counter, setCounter] = useState(
    decisionNodesAssignation.length
      ? decisionNodesAssignation[decisionNodesAssignation.length - 1].id + 1
      : 1,
  );

  const validateRanges = () => {
    if (newNode.ranges.length < 2) {
      return alert('Debes agregar al menos dos rangos');
    }
    const ranges = newNode.ranges;
    for (let i = 0; i < ranges.length; i++) {
      if (ranges[i].decisionNodeId === undefined || ranges[i].value === null) {
        if (i === ranges.length - 1) return true;
        return alert('Debes completar todos los rangos');
      }
    }
    return true;
  };
  const addNode = () => {
    if (selectedCategory !== 'decisionEngine') {
      if (!validateRanges()) return;
    }
    if (newNode.originNodeId === null) {
      return alert('Debes completar todos los camposa');
    }
    const nodes = [...decisionNodesAssignation];
    nodes.push({ ...newNode });
    const nextCounter = counter;
    setCounter(nextCounter + 1);
    setDecisionNodesAssignation(nodes);
    setOpenAddNode(false);
    setNewNode(NEW_NODE);
  };

  const addRange = () => {
    const range = {
      value: '',
      decisionNodeId: newNode.id,
    };
    setNewNode({ ...newNode, ranges: [...newNode.ranges, range] });
  };

  const setUpRangeForBoolean = () => {
    const ranges = [
      {
        value: '0',
        decisionNodeId: newNode.id,
        nodeId: 0,
      },
      {
        value: '1',
        decisionNodeId: newNode.id,
        nodeId: 1,
      },
    ];
    return ranges;
  };

  const handleChangeRange = (e: SelectChangeEvent<number>, index: number) => {
    const { value } = e.target;
    const { name } = e.target;
    const ranges = newNode.ranges.map((x, i) => {
      if (i === index) return { ...x, [name]: value };
      return x;
    });
    setNewNode({ ...newNode, ranges });
  };

  const handleChangeOrigin = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    const [originNodeIdStr, metricIdStr] = String(value).split('-');
    const originNodeId = Number(originNodeIdStr);
    const node = decisionNodes.find(
      (x) =>
        x.id === originNodeId &&
        String(x.metricId ?? '') === (metricIdStr ?? ''),
    );
    return setNewNode({
      ...newNode,
      originNodeId,
      value: '',
      type: node?.type || null,
      operation: node?.operation || 'gt',
      description: node?.description || '',
      name: node?.name || '',
      id: counter,
      ranges: node?.type === 'boolean' ? setUpRangeForBoolean() : [],
      metricId: node?.metricId ?? null,
    });
  };

  const handleChangeCategory = (e: SelectChangeEvent<string>) => {
    setSelectedCategory(e.target.value);
  };

  const deleteRange = (index: number) => {
    const ranges = newNode.ranges.filter((x, i) => i !== index);
    ranges[ranges.length - 1].max = null;
    setNewNode({ ...newNode, ranges });
  };

  const availableNodeForSelection = () => {
    const selectedPairs = decisionNodesAssignation
      .filter((n) => n.originNodeId != null)
      .map((n) => `${n.originNodeId}-${n.metricId ?? ''}`);
    return decisionNodes.filter((x) => {
      if (x.type === 'final') return false;
      const pair = `${x.id}-${x.metricId ?? ''}`;
      return (
        !selectedPairs.includes(pair) &&
        x.category === selectedCategory &&
        Number(x.subDecisionEngineId) !== Number(id)
      );
    });
  };

  const availableFinalNodeForSelection = () => {
    return decisionNodes.filter((x) => x.type === 'final');
  };

  return (
    <Dialog
      maxWidth="md"
      open={openAddNode}
      onClose={() => setOpenAddNode(false)}
    >
      <DialogTitle>Agregar evaluación</DialogTitle>
      <DialogContent style={{ width: '500px' }}>
        <Typography variant="body1" gutterBottom>
          Acá podrás definir una nueva evaluación dentro de la matriz.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <b>Evaluación a agregar</b>
        </Typography>
        <div style={{ padding: '10px 0px' }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="filter-label">Categorías</InputLabel>
            <Select
              labelId="filter-label"
              value={selectedCategory}
              onChange={handleChangeCategory}
              label="Categorías"
            >
              {Object.keys(categorizedNodes).map((x, index) => (
                <MenuItem key={index} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: '10px 0px' }}>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={selectedCategory === ''}
          >
            <InputLabel id="filter-label">Evaluaciones disponibles</InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              value={
                newNode.originNodeId != null
                  ? `${newNode.originNodeId}-${newNode.metricId ?? ''}`
                  : ''
              }
              onChange={handleChangeOrigin}
              label="Evaluaciones disponibles"
            >
              {availableNodeForSelection().map((x, index) => (
                <MenuItem key={index} value={`${x.id}-${x.metricId ?? ''}`}>
                  {x.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {newNode.originNodeId && (
          <div style={{ padding: '20ox' }}>
            <Typography variant="body1" gutterBottom>
              {newNode.description}
            </Typography>
          </div>
        )}
        {newNode.type !== 'decisionEngine' && newNode.originNodeId && (
          <RangesAssign
            newNode={newNode}
            setNewNode={setNewNode}
            addRange={addRange}
            handleChangeRange={handleChangeRange}
            deleteRange={deleteRange}
            availableFinalNodeForSelection={availableFinalNodeForSelection}
            operation={operation}
            setOperation={setOperation}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={() => setOpenAddNode(false)}
          color="secondary"
          variant="contained"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => addNode()}
          color="primary"
          variant="contained"
        >
          Agregar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewMatrixAssignation;
